import { useEffect, useRef } from 'react';

const VERT = `
attribute vec2 a_pos;
varying vec2 v_uv;
void main() {
  v_uv = a_pos * 0.5 + 0.5;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

const FRAG = `
precision highp float;
varying vec2 v_uv;
uniform vec2 u_res;
uniform float u_time;
uniform vec2 u_mouse;
uniform vec2 u_trail;
uniform float u_energy;

vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x * 34.0) + 1.0) * x); }

float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i = floor(v + dot(v, C.yy));
  vec2 x0 = v - i + dot(i, C.xx);
  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;
  i = mod289(i);
  vec3 p = permute(permute(i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));
  vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
  m = m * m;
  m = m * m;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
  vec3 g;
  g.x = a0.x * x0.x + h.x * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  mat2 rot = mat2(0.8, 0.6, -0.6, 0.8);
  for (int i = 0; i < 4; i++) {
    v += a * snoise(p);
    p = rot * p * 2.02 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v;
}

void main() {
  vec2 uv = v_uv;
  float aspect = u_res.x / max(u_res.y, 1.0);
  vec2 p = vec2(uv.x * aspect, uv.y) * 1.65;

  vec2 mPos = vec2(u_mouse.x * aspect, u_mouse.y) * 1.65;
  vec2 tPos = vec2(u_trail.x * aspect, u_trail.y) * 1.65;
  vec2 flow = mPos - tPos;

  float dMouse = length(p - mPos);
  float dTrail = length(p - tPos);
  float influence = exp(-dMouse * dMouse * 3.2) + 0.65 * exp(-dTrail * dTrail * 1.8);
  vec2 swirl = vec2(-(p.y - mPos.y), p.x - mPos.x) * exp(-dMouse * 3.0) * (0.28 + u_energy * 0.45);
  p -= flow * influence * 0.85 - swirl;

  float t = u_time * 0.14;
  vec2 q = vec2(
    fbm(p + vec2(0.0, t * 0.9)),
    fbm(p + vec2(5.2, 1.3 - t * 0.7))
  );
  vec2 r = vec2(
    fbm(p + 2.6 * q + vec2(1.7, 9.2) + 0.22 * t),
    fbm(p + 2.6 * q + vec2(8.3, 2.8) - 0.19 * t)
  );
  float f = fbm(p + 2.2 * r);

  float silk = smoothstep(-0.65, 0.75, f);
  float ridge = pow(1.0 - abs(f * 1.35), 3.0);
  float rightPresence = mix(0.48, 1.0, smoothstep(0.05, 0.72, uv.x));
  float bottomFade = smoothstep(0.0, 0.28, uv.y);
  float topSoft = smoothstep(1.0, 0.86, uv.y);
  float mask = bottomFade * mix(0.75, 1.0, topSoft) * rightPresence;

  vec3 paper = vec3(0.9608, 0.9569, 0.9373);
  vec3 pearl = vec3(0.992, 0.989, 0.976);
  vec3 stone = vec3(0.845, 0.832, 0.792);
  vec3 charcoal = vec3(0.54, 0.525, 0.482);

  vec3 fluid = mix(paper, stone, silk * 0.78);
  fluid = mix(fluid, charcoal, smoothstep(0.45, 0.98, length(q)) * 0.46);
  fluid = mix(fluid, pearl, clamp(ridge * 0.52 + influence * 0.25, 0.0, 0.75));

  float grain = (fract(sin(dot(gl_FragCoord.xy + fract(u_time), vec2(12.9898, 78.233))) * 43758.5453) - 0.5) * 0.016;
  vec3 col = mix(paper, fluid + grain, mask);

  gl_FragColor = vec4(col, 1.0);
}
`;

export default function LiminalMatrix() {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent)) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const gl = canvas.getContext('webgl', { alpha: false, antialias: false, depth: false });
    if (!gl) return undefined;

    const compile = (type, src) => {
      const sh = gl.createShader(type);
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      return sh;
    };
    const prog = gl.createProgram();
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    const posLoc = gl.getAttribLocation(prog, 'a_pos');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const uRes = gl.getUniformLocation(prog, 'u_res');
    const uTime = gl.getUniformLocation(prog, 'u_time');
    const uMouse = gl.getUniformLocation(prog, 'u_mouse');
    const uTrail = gl.getUniformLocation(prog, 'u_trail');
    const uEnergy = gl.getUniformLocation(prog, 'u_energy');

    const pointer = { x: 0.72, y: 0.55, tx: 0.72, ty: 0.55, lx: 0.72, ly: 0.55, energy: 0 };
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    const resize = () => {
      const layer = canvas.parentElement;
      const hero = layer.parentElement;
      layer.style.setProperty('--hero-top', `${Math.max(0, hero.getBoundingClientRect().top + window.scrollY)}px`);
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      gl.viewport(0, 0, canvas.width, canvas.height);
    };
    resize();
    const header = document.querySelector('header');
    const headerObserver = header && window.ResizeObserver ? new ResizeObserver(resize) : null;
    headerObserver?.observe(header, { box: 'border-box' });

    const onMove = e => {
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const nx = (e.clientX - rect.left) / rect.width;
      const ny = 1.0 - (e.clientY - rect.top) / rect.height;
      if (ny >= -0.2 && ny <= 1.2) {
        pointer.tx = Math.max(0, Math.min(1, nx));
        pointer.ty = Math.max(0, Math.min(1, ny));
        pointer.energy = Math.min(1, pointer.energy + 0.18);
      }
    };

    let raf = 0;
    const start = performance.now();
    const render = now => {
      const t = reduced ? 1.5 : (now - start) * 0.001;
      pointer.x += (pointer.tx - pointer.x) * 0.08;
      pointer.y += (pointer.ty - pointer.y) * 0.08;
      pointer.lx += (pointer.x - pointer.lx) * 0.032;
      pointer.ly += (pointer.y - pointer.ly) * 0.032;
      pointer.energy *= 0.94;

      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.uniform1f(uTime, t);
      gl.uniform2f(uMouse, pointer.x, pointer.y);
      gl.uniform2f(uTrail, pointer.lx, pointer.ly);
      gl.uniform1f(uEnergy, pointer.energy);
      gl.drawArrays(gl.TRIANGLES, 0, 6);

      if (!reduced) raf = requestAnimationFrame(render);
    };

    window.addEventListener('resize', resize);
    window.addEventListener('pointermove', onMove, { passive: true });
    raf = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(raf);
      headerObserver?.disconnect();
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onMove);
    };
  }, []);

  return (
    <div className="hero-fluid" aria-hidden="true">
      <canvas ref={canvasRef} className="hero-fluid-canvas" />
    </div>
  );
}
